import { Button, Chip, Group, Input, Stack, Text, Tooltip } from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import { IconRoute } from '@tabler/icons-react'
import { FormattedMessage, useIntl } from 'react-intl'
import { PATROL_RESERVED_TAG_CONFIG } from '../vlmPipelineConfig'
import { PipelineFormValues } from './VlmPipelineForm'

type ActionAlertTagsProps = {
  form: UseFormReturnType<PipelineFormValues>
  index: number
}

export const ActionAlertTags = ({ form, index }: ActionAlertTagsProps) => {
  const intl = useIntl()
  const alertPrompts = form.values.alertPrompts
  const action = form.values.actions[index]

  const selectedTags = action.alert_tags
  const tagsError = form.errors[`actions.${index}.alert_tags`]

  // Only email actions of virtual patrols can deliver the round summary.
  const reservedTags =
    action.type === 'email' && form.values.pipelineType === 'virtual_patrol'
      ? PATROL_RESERVED_TAG_CONFIG
      : []
  const allTags = [
    ...reservedTags.map(({ tag }) => tag),
    ...alertPrompts.map((alertPrompt) => alertPrompt.alert_tag)
  ]

  const isAllSelected =
    allTags.length > 0 && allTags.every((tag) => selectedTags.includes(tag))

  const handleToggleAll = () => {
    form.setFieldValue(
      `actions.${index}.alert_tags`,
      isAllSelected ? [] : allTags
    )
  }

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Text size="xs" fw={500} c="dimmed">
          <FormattedMessage id="vlm.pipelines.create.actions.tags.label" />
        </Text>

        {allTags.length > 0 && (
          <Button variant="subtle" size="compact-xs" onClick={handleToggleAll}>
            <FormattedMessage
              id={
                isAllSelected
                  ? 'vlm.pipelines.create.actions.tags.deselectAll'
                  : 'vlm.pipelines.create.actions.tags.selectAll'
              }
            />
          </Button>
        )}
      </Group>

      <Input.Wrapper error={tagsError}>
        {allTags.length === 0 ? (
          <Text size="xs" c="dimmed">
            <FormattedMessage id="vlm.pipelines.create.actions.tags.empty" />
          </Text>
        ) : (
          <Chip.Group
            value={selectedTags}
            multiple
            onChange={(value) =>
              form.setFieldValue(`actions.${index}.alert_tags`, value)
            }
          >
            <Group gap="xs" mb={tagsError ? 'xs' : 0}>
              {reservedTags.map(({ tag, labelId, tooltipId }) => (
                <Tooltip
                  key={tag}
                  label={intl.formatMessage({ id: tooltipId })}
                  maw={260}
                  multiline
                  withArrow
                >
                  <Chip value={tag} size="sm" variant="outline" color="indigo">
                    <Group gap={4} wrap="nowrap">
                      <IconRoute size={12} />
                      <span>
                        <FormattedMessage id={labelId} />
                      </span>
                    </Group>
                  </Chip>
                </Tooltip>
              ))}
              {alertPrompts.map((alertPrompt) => (
                <Chip
                  key={alertPrompt.uuid}
                  value={alertPrompt.alert_tag}
                  size="sm"
                >
                  {alertPrompt.name}
                </Chip>
              ))}
            </Group>
          </Chip.Group>
        )}
      </Input.Wrapper>
    </Stack>
  )
}
